import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/BlogPost.css";

// ── Audio embed (Spotify) ────────────────────────────────────────────────

function AudioEmbed({ src, title }) {
  const audioRef = useRef(null);
  const [isFloating, setIsFloating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!audioRef.current) return;

      const rect = audioRef.current.getBoundingClientRect();

      // Once the player has mostly scrolled off screen,
      // turn it into a small floating player.
      if (rect.bottom < 80 && isPlaying) {
        setIsFloating(true);
      } else if (rect.top > window.innerHeight - 100) {
        setIsFloating(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isPlaying]);

  return (
    <>
      {/* Main player location inside the article */}
      <div
        ref={audioRef}
        className={`blog-video-container ${
          isFloating ? "video-placeholder" : ""
        }`}
      >
        <div className="blog-video-card">
          <div className="blog-video-header">
            <div>
              <span className="blog-video-label">IDENTITY AT THE CENTER</span>
              <h2>{title}</h2>
            </div>

            <span className="blog-video-duration">Episode #450</span>
          </div>

          <div className="blog-audio-wrap">
            <iframe
              className="blog-audio-frame"
              src={src}
              title={title}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              onLoad={() => setIsPlaying(true)}
            />
          </div>

          <div className="blog-video-caption">
            <span>▶</span>
            Listen to Episode #450 on Spotify — also on Apple Podcasts and identityatthecenter.com.
          </div>
        </div>
      </div>

      {/* Floating player */}
      {isFloating && (
        <div className="floating-video floating-audio">
          <div className="floating-video-header">
            <span>Identity at the Center</span>

            <button
              className="floating-video-close"
              onClick={() => setIsFloating(false)}
              aria-label="Close floating player"
            >
              ×
            </button>
          </div>

          <iframe
            src={src}
            title={title}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
        </div>
      )}
    </>
  );
}

// ── Blog post ───────────────────────────────────────────────────────────

function BlogPost17() {
  return (
    <>
      <Navbar />

      <article className="blog-post">

        {/* ── Breadcrumb ── */}
        <p className="blog-breadcrumb">
          <span>Podcast & Career</span>
          <span className="breadcrumb-divider">|</span>
          <span className="breadcrumb-section">
            IAM | Identity & Access Management | Behind the Scenes
          </span>
        </p>

        {/* ── Headline & dek ── */}
        <h1 className="blog-headline">
          I Was on the Identity at the Center Podcast! Behind the Scenes
          You Should Know
        </h1>

        <p className="blog-dek">
          A cold outreach message turned into a trip to Identiverse, a
          crash course in Identity and Access Management, and a seat on
          one of the biggest IAM podcasts in the industry.
        </p>

        {/* ── Share row ── */}
        <div className="blog-meta-row">
          <button
            className="blog-share-btn"
            onClick={() =>
              navigator.share?.({
                title: document.title,
                url: window.location.href,
              })
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        </div>

        {/* ── Date + read time ── */}
        <p className="blog-dateline">
          Episode #450 &nbsp;|&nbsp; 3 min read
        </p>

        {/* ── Embedded player ── */}
        <AudioEmbed
          src="https://open.spotify.com/embed/show/2vjnuzsgxjvIWWUAjYgoRy?utm_source=generator"
          title="Identity at the Center — Episode #450"
        />

        {/* ── Body copy ── */}
        <div className="blog-body">

          <p className="blog-drop-cap">
            I got the exciting opportunity to be a guest on the Identity
            at the Center Podcast — Episode #450! You can find it at{" "}
            <a
              href="https://www.identityatthecenter.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              identityatthecenter.com
            </a>
            , or stream it on Spotify and Apple Podcasts, wherever you
            get your podcasts. I'm thrilled to finally share the
            conversation between Jeff and me with all of you.
          </p>

          <p>
            Jeff and Jim (along with various other amazing people who
            work at RSM) have helped me out so much over the past three
            months, and honestly, they've been the catalyst for my
            growing interest in Identity and Access Management (IAM). As
            a technology risk consultant, IAM touches a huge portion of
            the work and testing I do day to day. So, getting the chance
            to go deeper on the subject with two people who live and
            breathe it was an incredible opportunity. This episode, #450,
            showcases a discussion between Jeff and I.
          </p>

          <p>
            Funny enough, Jeff and Jim work at the same firm I do. I'd
            started listening to the podcast thanks to a firm-wide email
            link. I quickly became a fan of the podcast before any of
            this happened, tuning in episode after episode without ever
            expecting our paths would cross professionally. So when I
            finally decided to reach out, it felt a little surreal.
          </p>

          <p>
            I sent a message through the "Contact" form on their
            website. That one message turned into multiple conversations
            with Jeff and Jim, which turned into an opportunity to travel
            to <strong>Identiverse in Las Vegas</strong>, and ultimately
            led me to learning a genuinely timely and important subject:
            Identity and Access Management.
          </p>

          <p>
            The episode covers a lot of ground. We talk about ideas on
            how to actually learn IAM as someone starting out, to the
            fundamentals of Role Based Access, all the way to a
            lighthearted detour into "Flamingo Flocking." It's a mix of
            practical, career-relevant insight and the kind of fun,
            off-the-cuff moments that make this podcast special.
          </p>

        </div>

        <div className="blog-body">

          <p className="blog-takeaway">
            <strong>Takeaway:</strong> The best opportunities sometimes
            start with taking a chance. You then can learn some really
            cool things. If you're curious about breaking into IAM, or
            just want to hear about trying to become a professional in
            the tech industry, the power of networking, what is on the
            horizon when it comes to the intersection of IAM and AI, and
            how many tabs on a computer is actually appropriate to have
            opened at once, give Episode #450 a listen above. I had a
            blast recording it, and I hope it comes through in the
            conversation. Thank you Jeff and Jim for making this episode
            possible.
          </p>

        </div>

        {/* ── Closing image ── */}
        <div className="blog-closing-image">
          <img
            src="/idac_podcast.png"
            alt="Identity at the Center podcast cover art"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "100%",
              display: "block",
            }}
          />
        </div>

      </article>

      <Footer />
    </>
  );
}

export default BlogPost17;